// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

/**
 * Password input with a show/hide toggle (Patrick 2026-08-18: pharmacy staff need to
 * see what they typed — mistyped passwords on a shared dispensing-floor workstation
 * were causing avoidable lockouts).
 *
 * Deliberate choices:
 *  - ALWAYS starts hidden, on every mount. The revealed state is never persisted to
 *    storage and never lifted out of this component, so a screen left open cannot come
 *    back showing a password.
 *  - The toggle is a SIBLING of the input, never a wrapper around it. Password managers
 *    and the browser's own autofill key off the input element and its autoComplete
 *    attribute; wrapping it in extra structure is what breaks them.
 *  - type=button so it can never submit the form it lives in.
 *  - The button is excluded from the tab order (tabIndex -1). Tabbing from the password
 *    field should reach the submit button, which is what someone typing a password
 *    expects; the toggle stays reachable by mouse and by screen readers.
 *
 * Callers keep ownership of value/onChange and pass their own field styling, so this
 * drops into the existing hand-styled auth forms without restyling them.
 */
export default function PasswordField({
  id,
  value,
  onChange,
  autoComplete,
  disabled,
  required,
  minLength,
  style,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  autoComplete: 'current-password' | 'new-password'
  disabled?: boolean
  required?: boolean
  minLength?: number
  style?: React.CSSProperties
}) {
  const [shown, setShown] = useState(false)
  const labelId = useId()

  return (
    <div style={{ position: 'relative', display: 'grid' }}>
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        minLength={minLength}
        // Reserve room for the button so a long password never runs underneath it.
        style={{ ...style, paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setShown(s => !s)}
        disabled={disabled}
        tabIndex={-1}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        aria-describedby={labelId}
        title={shown ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          border: 'none',
          background: 'transparent',
          color: 'var(--color-text-muted, #6b7280)',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {shown ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      </button>
      <span id={labelId} style={{ display: 'none' }}>
        Toggles whether your password is visible on screen.
      </span>
    </div>
  )
}
